import * as THREE from 'three';
import type { World } from '../sim/world';
import type { BatteryAim, Gun } from '../sim/cannons';
import { FLIGHT_DT, flyShot, makeFall, muzzleDesign, muzzleSpeed, shotDrag, SHOT_LIFE, type Plane } from '../sim/gunnery';
import { CANNONS } from '../data/ship';
import { Vec3 } from '../core/math';
import type { Pipeline } from './pipeline';

// The aim of a broadside, drawn over the finished picture: a ring on the sea
// the size of the expected fall of shot, the path each gun's ball would take
// fired as she's laid now (on the roll, as her captain fires it), where each
// would come down, and where a ship near the mark will be by the time the
// shot gets there. Drawn after the post chain, so it stays sharp and the same
// brightness by day or night; the scene's own depth hides it behind ships.

/** Guns a side (the most any class carries). */
const GUNS = CANNONS.length / 2;
/** Points each path is drawn with (the flight is flown step by step, then sampled). */
const PATH_POINTS = 40;

/** Display colours (drawn after tone mapping): laid, laying, can't bear. */
const INK = new THREE.Color(0.93, 0.9, 0.8);
const BRASS = new THREE.Color(0.9, 0.72, 0.4);
const DANGER = new THREE.Color(0.95, 0.47, 0.34);

// The ring is painted on the sea itself (so it rides the waves and hides
// behind hulls), but only inside its own box on screen: toward the horizon
// a pixel spans kilometres of sea, and a test made from screen derivatives
// alone would find the ring in every pixel along it. The small marks (the
// centre, where each ball falls, the lead) are drawn on screen at their
// projected places, a fixed size at any distance.
const DECAL_FRAG = /* glsl */ `
uniform sampler2D uDepth;
uniform sampler2D uOpaqueDepth;
uniform mat4 uInvViewProj;
uniform vec2 uScreen;
uniform float uPx;
uniform vec3 uRing;
uniform vec4 uRingBox;
uniform float uPulse;
uniform vec3 uColor;
uniform float uOpacity;
uniform float uDash;
uniform float uSpin;
uniform float uCross;
uniform vec3 uPip;
uniform vec4 uFalls[${GUNS}];
uniform vec4 uLead;
uniform float uLeadOn;
in vec2 vUv;

/** Distance in pixels from p to the ellipse about c with half-axes h (screen). */
float ellipse(vec2 p, vec2 c, vec2 h) {
  vec2 d = (p - c) / h;
  float e = length(d);
  vec2 g = d / (h * max(e, 1e-4));
  return abs(e - 1.0) / max(length(g), 1e-4);
}

float segment(vec2 p, vec2 a, vec2 b, out float t) {
  vec2 ab = b - a;
  t = clamp(dot(p - a, ab) / max(dot(ab, ab), 1e-6), 0.0, 1.0);
  return length(p - a - ab * t);
}

void main() {
  vec2 px = gl_FragCoord.xy;
  vec2 uv = px / uScreen;
  float d = texture(uDepth, uv).r;
  float od = texture(uOpaqueDepth, uv).r;
  // Only the sea: the water drew over whatever lay behind it (or over nothing).
  if (d >= 1.0 || d >= od - 1e-7) discard;
  float bright = 0.0, halo = 0.0, fill = 0.0;
  float w = 0.75 * uPx;

  // The ring's place and size on screen.
  vec2 bc = (uRingBox.xy + uRingBox.zw) * 0.5;
  vec2 bh = max((uRingBox.zw - uRingBox.xy) * 0.5, vec2(1.0));
  bool flat_ = bh.y < 3.5 * uPx;

  // The ring, on the water, within its box on screen (and a margin). Seen
  // nearly edge-on it's drawn as a flattened ellipse on screen instead.
  vec2 m = vec2(3.0 * uPx);
  if (flat_) {
    float e = ellipse(px, bc, vec2(bh.x, 3.5 * uPx));
    bright = 1.0 - smoothstep(w, w + 1.0, e);
    halo = 1.0 - smoothstep(w + 1.0, w + 1.0 + 2.5 * uPx, e);
  } else if (all(greaterThan(px, uRingBox.xy - m)) && all(lessThan(px, uRingBox.zw + m))) {
    vec4 wp = uInvViewProj * vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
    vec2 q = wp.xz / wp.w;
    float R = uRing.z;
    float r = length(q - uRing.xy);
    float mr = max(fwidth(r), 1e-4);
    float e = abs(r - R) / mr;
    float ang = atan(q.y - uRing.y, q.x - uRing.x) / 6.2831853;
    float dash = mix(1.0, step(0.42, fract(ang * 40.0 + uSpin)), uDash);
    bright = (1.0 - smoothstep(w, w + 1.0, e)) * dash;
    halo = (1.0 - smoothstep(w + 1.0, w + 1.0 + 2.5 * uPx, e)) * dash;
    // A wash inside, a little stronger toward the edge.
    fill = (1.0 - smoothstep(R - mr, R + mr, r)) * (0.035 + 0.06 * smoothstep(0.4 * R, R, r));
  }

  // Four short ticks just outside it, pointing in: they keep the mark
  // readable however small the ring is.
  {
    vec2 hb = vec2(max(bh.x, 7.0 * uPx), max(bh.y, 3.5 * uPx)) + 4.0 * uPx;
    vec2 dd = px - bc;
    float len = 5.0 * uPx;
    float tx = max(abs(abs(dd.x) - hb.x - len * 0.5) - len * 0.5, abs(dd.y));
    float ty = max(abs(abs(dd.y) - hb.y - len * 0.5) - len * 0.5, abs(dd.x));
    float tk = min(tx, ty);
    bright = max(bright, 0.85 * (1.0 - smoothstep(w, w + 1.0, tk)));
    halo = max(halo, 1.0 - smoothstep(w + 1.0, w + 1.0 + 2.5 * uPx, tk));
  }

  // The guns come on: one ripple outward.
  if (uPulse >= 0.0) {
    float k = 1.0 + 1.6 * uPulse;
    float e = ellipse(px, bc, max(bh, vec2(7.0 * uPx, 3.5 * uPx)) * k);
    float a = (1.0 - uPulse) * (1.0 - uPulse);
    bright = max(bright, a * (1.0 - smoothstep(w, w + 1.5, e)));
  }

  // The centre: a pip, or a cross where the guns can't be laid.
  if (uPip.z > 0.0) {
    vec2 dc = px - uPip.xy;
    float c = length(dc);
    float pip = (1.0 - smoothstep(1.3 * uPx, 1.3 * uPx + 1.0, c)) * (1.0 - uCross);
    float arm = min(abs(dc.x - dc.y), abs(dc.x + dc.y)) * 0.7071;
    float reach = 1.0 - smoothstep(6.0 * uPx, 6.0 * uPx + 1.0, c);
    float cross = uCross * (1.0 - smoothstep(0.7 * uPx, 0.7 * uPx + 1.0, arm)) * reach;
    bright = max(bright, max(pip, cross));
    halo = max(halo, (1.0 - smoothstep(1.3 * uPx + 1.0, 4.0 * uPx, c)) * (1.0 - uCross));
    halo = max(halo, uCross * (1.0 - smoothstep(0.7 * uPx + 1.0, 3.0 * uPx, arm)) * (1.0 - smoothstep(6.0 * uPx, 7.5 * uPx, c)));
  }

  // Where each gun would put her ball.
  for (int i = 0; i < ${GUNS}; i++) {
    vec4 f = uFalls[i];
    if (f.z <= 0.0) continue;
    float fd = length(px - f.xy);
    bright = max(bright, f.z * (1.0 - smoothstep(1.1 * uPx, 1.1 * uPx + 1.0, fd)));
    halo = max(halo, f.z * (1.0 - smoothstep(1.1 * uPx + 1.0, 3.2 * uPx, fd)));
  }

  // Lead: a ship near the mark, from where she is to where she'll be.
  if (uLeadOn > 0.0) {
    float t;
    float ld = segment(px, uLead.xy, uLead.zw, t);
    float span = length(uLead.zw - uLead.xy);
    float dots = step(0.5, fract(t * span / (5.0 * uPx)));
    float lead = dots * (1.0 - smoothstep(0.55 * uPx, 0.55 * uPx + 1.0, ld)) * 0.7;
    vec2 dl = abs(px - uLead.zw);
    float edge = abs(dl.x + dl.y - 4.5 * uPx);
    lead = max(lead, 1.0 - smoothstep(0.7 * uPx, 0.7 * uPx + 1.0, edge));
    bright = max(bright, uLeadOn * lead);
    halo = max(halo, uLeadOn * (1.0 - smoothstep(0.7 * uPx + 1.0, 3.0 * uPx, edge)));
  }

  // Premultiplied: the bright marks over a soft dark halo, and a faint wash.
  float shade = 0.45 * halo * (1.0 - bright);
  gl_FragColor = vec4(uColor * (bright + fill), clamp(bright + shade + fill, 0.0, 1.0)) * uOpacity;
}
`;

const PATH_VERT = /* glsl */ `
attribute vec3 aNext;
attribute float aSide;
attribute float aT;
attribute float aAlpha;
uniform vec2 uScreen;
uniform float uWidth;
out float vT;
out float vAlpha;
out float vAcross;
void main() {
  vec4 a = projectionMatrix * viewMatrix * vec4(position, 1.0);
  vec4 b = projectionMatrix * viewMatrix * vec4(aNext, 1.0);
  vT = aT;
  vAlpha = aAlpha;
  vAcross = aSide;
  if (a.w < 0.05 || b.w < 0.05) {
    // Behind the camera: nothing to draw.
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    vAlpha = 0.0;
    return;
  }
  vec2 sa = a.xy / a.w * uScreen * 0.5, sb = b.xy / b.w * uScreen * 0.5;
  vec2 dir = sb - sa;
  float len = length(dir);
  dir = len > 1e-4 ? dir / len : vec2(1.0, 0.0);
  a.xy += vec2(-dir.y, dir.x) * aSide * uWidth * 2.0 / uScreen * a.w;
  gl_Position = a;
}
`;

const PATH_FRAG = /* glsl */ `
uniform sampler2D uDepth;
uniform vec2 uScreen;
uniform vec3 uColor;
uniform float uOpacity;
uniform float uFlow;
uniform float uTime;
in float vT;
in float vAlpha;
in float vAcross;
void main() {
  float sd = texture(uDepth, gl_FragCoord.xy / uScreen).r;
  // Behind the ship (or anything else) there: drawn faint.
  float seen = gl_FragCoord.z <= sd + 2e-7 ? 1.0 : 0.3;
  float edge = 1.0 - smoothstep(0.3, 1.0, abs(vAcross));
  // Faint from the muzzle, strongest in mid-flight, gone just short of the
  // mark so the ring is left clear.
  float along = smoothstep(0.0, 0.12, vT) * (0.25 + 0.75 * vT) * (1.0 - smoothstep(0.86, 0.97, vT));
  // Laid: a slow glint running out along each path.
  float head = fract(uTime * 0.4);
  float glint = uFlow * 0.9 * exp(-pow((vT - head) * 14.0, 2.0));
  float a = vAlpha * edge * seen * (along + glint * along) * uOpacity;
  gl_FragColor = vec4(uColor * a, a);
}
`;

const premultiplied = {
  transparent: true,
  depthTest: false,
  depthWrite: false,
  blending: THREE.CustomBlending,
  blendSrc: THREE.OneFactor,
  blendDst: THREE.OneMinusSrcAlphaFactor,
} as const;

export class AimView {
  private readonly decal: THREE.Mesh;
  private readonly decalMat: THREE.ShaderMaterial;
  private readonly paths: THREE.Mesh;
  private readonly pathMat: THREE.ShaderMaterial;
  private readonly pos: THREE.BufferAttribute;
  private readonly next: THREE.BufferAttribute;
  private readonly alpha: THREE.BufferAttribute;
  private readonly falls: THREE.Vector4[] = [];
  private opacity = 0;
  private spin = 0;
  private time = 0;
  /** The colour shown, easing toward the state's; the ripple when the guns come on (-1 none); how laid they look. */
  private readonly color = INK.clone();
  private pulse = -1;
  private flow = 0;
  private wasLaid = false;
  /** A flight, step by step (x, y, z), before it's sampled down to PATH_POINTS. */
  private readonly flight = new Float32Array(Math.ceil(SHOT_LIFE * 60 + 4) * 3);
  private readonly fall = makeFall();
  private readonly plane: Plane = { ux: 0, uz: 0, R: 0 };

  constructor(private readonly pipeline: Pipeline, private readonly renderer: THREE.WebGLRenderer) {
    for (let i = 0; i < GUNS; i++) this.falls.push(new THREE.Vector4());
    const screen = new THREE.Vector2(1, 1);
    this.decalMat = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `out vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: DECAL_FRAG,
      uniforms: {
        uDepth: { value: null },
        uOpaqueDepth: { value: null },
        uInvViewProj: { value: new THREE.Matrix4() },
        uScreen: { value: screen },
        uPx: { value: 1 },
        uRing: { value: new THREE.Vector3() },
        uRingBox: { value: new THREE.Vector4() },
        uPulse: { value: -1 },
        uPip: { value: new THREE.Vector3() },
        uColor: { value: new THREE.Color() },
        uOpacity: { value: 0 },
        uDash: { value: 0 },
        uSpin: { value: 0 },
        uCross: { value: 0 },
        uFalls: { value: this.falls },
        uLead: { value: new THREE.Vector4() },
        uLeadOn: { value: 0 },
      },
      ...premultiplied,
    });
    const tri = new THREE.BufferGeometry();
    tri.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    tri.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
    this.decal = new THREE.Mesh(tri, this.decalMat);
    this.decal.frustumCulled = false;
    this.decal.visible = false;

    // Ribbons: two vertices a point, each pushed out across the path on screen.
    const n = GUNS * PATH_POINTS * 2;
    const g = new THREE.BufferGeometry();
    this.pos = new THREE.BufferAttribute(new Float32Array(n * 3), 3).setUsage(THREE.DynamicDrawUsage);
    this.next = new THREE.BufferAttribute(new Float32Array(n * 3), 3).setUsage(THREE.DynamicDrawUsage);
    this.alpha = new THREE.BufferAttribute(new Float32Array(n), 1).setUsage(THREE.DynamicDrawUsage);
    const side = new Float32Array(n), t = new Float32Array(n);
    const index: number[] = [];
    for (let p = 0; p < GUNS; p++) {
      for (let k = 0; k < PATH_POINTS; k++) {
        const v = (p * PATH_POINTS + k) * 2;
        side[v] = -1;
        side[v + 1] = 1;
        t[v] = t[v + 1] = k / (PATH_POINTS - 1);
        if (k < PATH_POINTS - 1) index.push(v, v + 1, v + 2, v + 1, v + 3, v + 2);
      }
    }
    g.setAttribute('position', this.pos);
    g.setAttribute('aNext', this.next);
    g.setAttribute('aAlpha', this.alpha);
    g.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
    g.setAttribute('aT', new THREE.BufferAttribute(t, 1));
    g.setIndex(index);
    this.pathMat = new THREE.ShaderMaterial({
      vertexShader: PATH_VERT,
      fragmentShader: PATH_FRAG,
      // The ribbons' winding on screen depends on which way each path runs.
      side: THREE.DoubleSide,
      uniforms: {
        uDepth: { value: null },
        uScreen: { value: screen },
        uWidth: { value: 1.5 },
        uColor: { value: new THREE.Color() },
        uOpacity: { value: 0 },
        uFlow: { value: 0 },
        uTime: { value: 0 },
      },
      ...premultiplied,
    });
    this.paths = new THREE.Mesh(g, this.pathMat);
    this.paths.frustumCulled = false;
    this.paths.visible = false;
    pipeline.overlay.add(this.paths, this.decal);
  }

  /**
   * Draw the player's aim (if she's laying a broadside) for this frame. `dt`
   * is real time; `night` (0 day .. 1 night) quietens it on a dark sea.
   */
  update(world: World, camera: THREE.PerspectiveCamera, dt: number, night = 0): void {
    const me = world.player;
    const b = me.battery;
    const aim = b.aim;
    this.opacity = aim ? Math.min(1, this.opacity + dt * 6) : Math.max(0, this.opacity - dt * 6);
    const on = this.opacity > 0 && aim !== null;
    this.decal.visible = this.paths.visible = on;
    if (!on) return;

    // The camera as it will be drawn this frame (its world matrix is otherwise brought up to date only in the render).
    camera.updateMatrixWorld();
    const px = this.renderer.getPixelRatio();
    const du = this.decalMat.uniforms, pu = this.pathMat.uniforms;
    (du.uScreen!.value as THREE.Vector2).set(this.pipeline.screenWidth, this.pipeline.screenHeight);
    du.uDepth!.value = pu.uDepth!.value = this.pipeline.sceneDepth;
    du.uOpaqueDepth!.value = this.pipeline.opaqueDepth;
    (du.uInvViewProj!.value as THREE.Matrix4).multiplyMatrices(camera.matrixWorld, camera.projectionMatrixInverse);
    du.uPx!.value = px;
    pu.uWidth!.value = 1.3 * px;
    du.uOpacity!.value = this.opacity * (1 - 0.25 * night);
    pu.uOpacity!.value = this.opacity * (1 - 0.45 * night);
    const R = Math.max(aim.spread, this.leastRadius(aim, camera, 5 * px));
    (du.uRing!.value as THREE.Vector3).set(aim.x, aim.z, R);
    this.ringBox(aim.x, aim.z, R, camera, du.uRingBox!.value as THREE.Vector4);
    const pip = this.toScreen(aim.x, 0, aim.z, camera);
    (du.uPip!.value as THREE.Vector3).set(pip.x, pip.y, pip.z);

    // Laid: pale; being laid: brass, turning; can't be laid there: red, with
    // a cross. The colour eases from one to the next.
    const bad = aim.status !== 'ok';
    const laying = !bad && aim.laid < aim.bearing;
    const laid = !bad && !laying && aim.bearing > 0;
    this.color.lerp(bad ? DANGER : laying ? BRASS : INK, 1 - Math.exp(-dt * 10));
    (du.uColor!.value as THREE.Color).copy(this.color);
    (pu.uColor!.value as THREE.Color).copy(this.color).multiplyScalar(0.88);
    du.uDash!.value = bad || laying ? 1 : 0;
    du.uCross!.value = bad ? 1 : 0;
    if (laying) this.spin = (this.spin + dt * 0.25) % 1;
    du.uSpin!.value = this.spin;
    // The moment the guns come on: one ripple, and the paths begin to glint.
    if (laid && !this.wasLaid) this.pulse = 0;
    this.wasLaid = laid;
    if (this.pulse >= 0) this.pulse = this.pulse + dt / 0.6 > 1 ? -1 : this.pulse + dt / 0.6;
    du.uPulse!.value = this.pulse;
    this.flow += ((laid ? 1 : 0) - this.flow) * (1 - Math.exp(-dt * 4));
    this.time += dt;
    pu.uFlow!.value = this.flow;
    pu.uTime!.value = this.time;

    this.updatePaths(world, aim, camera);
    this.updateLead(world, aim, camera);
  }

  /**
   * The radius (m) the ring needs to be `pixels` across where it lies, seen
   * from `camera`, measured across the line of sight: toward the horizon the
   * sea is foreshortened to nothing the other way, and a ring that size
   * deep would stretch the width of the picture.
   */
  private leastRadius(aim: BatteryAim, camera: THREE.PerspectiveCamera, pixels: number): number {
    const dx = camera.position.x - aim.x, dz = camera.position.z - aim.z;
    const d = Math.hypot(dx, dz) || 1;
    const a = ndcA.set(aim.x, 0, aim.z).project(camera);
    const b = ndcB.set(aim.x - dz / d, 0, aim.z + dx / d).project(camera);
    if (a.z > 1 || b.z > 1) return 0;
    const perMetre = Math.hypot((b.x - a.x) * this.pipeline.screenWidth, (b.y - a.y) * this.pipeline.screenHeight) / 2;
    return pixels / Math.max(perMetre, 1e-6);
  }

  /** A world point on screen, in the overlay's gl_FragCoord pixels; z is 0 if it's behind the camera. */
  private toScreen(x: number, y: number, z: number, camera: THREE.PerspectiveCamera): THREE.Vector3 {
    const p = ndcA.set(x, y, z).project(camera);
    const ok = p.z < 1 && Number.isFinite(p.x) && Number.isFinite(p.y);
    return ndcB.set((p.x * 0.5 + 0.5) * this.pipeline.screenWidth, (p.y * 0.5 + 0.5) * this.pipeline.screenHeight, ok ? 1 : 0);
  }

  /** The ring's box on screen (overlay pixels); all of it if any part is behind the camera. */
  private ringBox(cx: number, cz: number, R: number, camera: THREE.PerspectiveCamera, out: THREE.Vector4): void {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      const p = this.toScreen(cx + Math.cos(a) * R, 0, cz + Math.sin(a) * R, camera);
      if (!p.z) {
        out.set(-1e6, -1e6, 1e6, 1e6);
        return;
      }
      x0 = Math.min(x0, p.x);
      y0 = Math.min(y0, p.y);
      x1 = Math.max(x1, p.x);
      y1 = Math.max(y1, p.y);
    }
    out.set(x0, y0, x1, y1);
  }

  /** Each gun's path as she's laid now, fired on the roll (at her steady heel), and where it comes down. */
  private updatePaths(world: World, aim: BatteryAim, camera: THREE.PerspectiveCamera): void {
    const me = world.player, ship = me.ship, b = me.battery;
    const windX = world.wind.dirX * world.wind.speed, windZ = world.wind.dirZ * world.wind.speed;
    const pos = this.pos.array as Float32Array, next = this.next.array as Float32Array, alpha = this.alpha.array as Float32Array;
    const off = (ship.heel - b.steadyHeel) * aim.side;
    let p = 0;
    for (const g of b.guns) {
      if (g.spec.side !== aim.side || g.destroyed || p >= GUNS) continue;
      const charge = g.loaded ? g.charge : b.ammo;
      const muzzle = ship.designToWorld(muzzleDesign(g.spec, tmpA), tmpM);
      const dir = ship.rot.rotate(tmpD.set(Math.tan(g.train), g.elevation + off, g.spec.side).normalize());
      tmpV.copy(dir).scale(muzzleSpeed(g.spec, charge)).add(ship.vel);
      let n = 0;
      const f = this.flight;
      // Laid high (on a ship's hull or rigging), the path ends where it passes over the mark.
      const dx = aim.x - muzzle.x, dz = aim.z - muzzle.z, R = Math.hypot(dx, dz);
      const plane = aim.height > 0 && R > 1 ? Object.assign(this.plane, { ux: dx / R, uz: dz / R, R }) : undefined;
      flyShot(muzzle, tmpV, shotDrag(charge, b.weight), windX, windZ, FLIGHT_DT, this.fall, (x, y, z) => {
        if (n * 3 + 2 < f.length) (f[n * 3] = x), (f[n * 3 + 1] = y), (f[n * 3 + 2] = z), n++;
      }, plane);
      const strength = strengthOf(g);
      const at = this.toScreen(this.fall.x, 0, this.fall.z, camera);
      this.falls[p]!.set(at.x, at.y, this.fall.ok && at.z ? strength : 0, 0);
      // Sampled evenly along the flight, ends kept.
      for (let k = 0; k < PATH_POINTS; k++) {
        const s = Math.min(n - 1, Math.round((k / (PATH_POINTS - 1)) * (n - 1)));
        const s2 = k < PATH_POINTS - 1 ? Math.min(n - 1, Math.round(((k + 1) / (PATH_POINTS - 1)) * (n - 1))) : s;
        const v = (p * PATH_POINTS + k) * 2;
        for (let j = 0; j < 2; j++) {
          pos[(v + j) * 3] = f[s * 3]!;
          pos[(v + j) * 3 + 1] = f[s * 3 + 1]!;
          pos[(v + j) * 3 + 2] = f[s * 3 + 2]!;
          if (s2 !== s) {
            next[(v + j) * 3] = f[s2 * 3]!;
            next[(v + j) * 3 + 1] = f[s2 * 3 + 1]!;
            next[(v + j) * 3 + 2] = f[s2 * 3 + 2]!;
          } else {
            // The last point: carry the last leg on past it.
            const s0 = Math.max(0, s - 1);
            next[(v + j) * 3] = 2 * f[s * 3]! - f[s0 * 3]!;
            next[(v + j) * 3 + 1] = 2 * f[s * 3 + 1]! - f[s0 * 3 + 1]!;
            next[(v + j) * 3 + 2] = 2 * f[s * 3 + 2]! - f[s0 * 3 + 2]!;
          }
          alpha[v + j] = strength * 0.55;
        }
      }
      p++;
    }
    for (let i = p; i < GUNS; i++) this.falls[i]!.set(0, 0, 0, 0);
    this.paths.geometry.setDrawRange(0, p * (PATH_POINTS - 1) * 6);
    this.pos.needsUpdate = this.next.needsUpdate = this.alpha.needsUpdate = true;
  }

  /** A ship our lookouts can see near the mark: where she'll be when the shot arrives. */
  private updateLead(world: World, aim: BatteryAim, camera: THREE.PerspectiveCamera): void {
    const du = this.decalMat.uniforms;
    // Laid on a ship: from the part of her aimed at to where the guns lead it.
    const lock = world.aimTarget;
    if (lock) {
      const now = this.toScreen(lock.x, 0, lock.z, camera);
      const nx = now.x, ny = now.y, seen = now.z;
      const then = this.toScreen(aim.x, 0, aim.z, camera);
      (du.uLead!.value as THREE.Vector4).set(nx, ny, then.x, then.y);
      du.uLeadOn!.value = seen && then.z && Math.hypot(aim.x - lock.x, aim.z - lock.z) > 2 ? 1 : 0;
      return;
    }
    let best: World['vessels'][number] | null = null, bd = Infinity;
    for (const v of world.vessels) {
      if (v === world.player || v.ship.sunk || !world.lookout.get(v.id)?.visible) continue;
      const d = Math.hypot(v.ship.com.x - aim.x, v.ship.com.z - aim.z);
      if (d < Math.max(90, 30 * v.ship.sx + 2 * aim.spread) && d < bd) (bd = d), (best = v);
    }
    const t = aim.tof;
    if (!best || Math.hypot(best.ship.vel.x, best.ship.vel.z) * t < 2) {
      du.uLeadOn!.value = 0;
      return;
    }
    const s = best.ship;
    const now = this.toScreen(s.com.x, 0, s.com.z, camera);
    const nx = now.x, ny = now.y, seen = now.z;
    const then = this.toScreen(s.com.x + s.vel.x * t, 0, s.com.z + s.vel.z * t, camera);
    (du.uLead!.value as THREE.Vector4).set(nx, ny, then.x, then.y);
    du.uLeadOn!.value = seen && then.z ? 1 : 0;
  }
}

/** How strongly a gun's path is drawn: loaded and manned; loaded; still being loaded. */
function strengthOf(g: Gun): number {
  return g.loaded ? (g.crew > 0.2 ? 1 : 0.6) : 0.3;
}

const tmpA = new Vec3(), tmpM = new Vec3(), tmpD = new Vec3(), tmpV = new Vec3();
const ndcA = new THREE.Vector3(), ndcB = new THREE.Vector3();
